import { useRef, useEffect, useState } from 'react';
import { TrendingUp, AlertOctagon, CheckCircle2, XCircle } from 'lucide-react';
import type { RepositoryAnalysis } from '../types/analysis.types';

interface InsightsGridProps {
  data: RepositoryAnalysis;
}

export const InsightsGrid: React.FC<InsightsGridProps> = ({ data }) => {
  const [showStrengthsOverflow, setShowStrengthsOverflow] = useState(false);
  const [showGapsOverflow, setShowGapsOverflow] = useState(false);
  const strengthsListRef = useRef<HTMLUListElement>(null);
  const gapsListRef = useRef<HTMLUListElement>(null);

  // Check if lists have overflow
  useEffect(() => {
    // Capture ref values at setup time to avoid stale refs in cleanup
    // React refs can change between effect run and cleanup, causing listener leaks
    const strengthsEl = strengthsListRef.current;
    const gapsEl = gapsListRef.current;

    const checkOverflow = (
      element: HTMLUListElement | null,
      setter: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      if (element) {
        const hasOverflow = element.scrollHeight > element.clientHeight;
        const isScrolledToBottom =
          element.scrollHeight - element.scrollTop <= element.clientHeight + 5;
        setter(hasOverflow && !isScrolledToBottom);
      }
    };

    const handleStrengthsScroll = () => checkOverflow(strengthsEl, setShowStrengthsOverflow);
    const handleGapsScroll = () => checkOverflow(gapsEl, setShowGapsOverflow);

    // Initial check
    handleStrengthsScroll();
    handleGapsScroll();

    // Add listeners using captured element references
    strengthsEl?.addEventListener('scroll', handleStrengthsScroll);
    gapsEl?.addEventListener('scroll', handleGapsScroll);
    window.addEventListener('resize', handleStrengthsScroll);
    window.addEventListener('resize', handleGapsScroll);

    return () => {
      // Use captured element references for cleanup (not ref.current which may be stale)
      strengthsEl?.removeEventListener('scroll', handleStrengthsScroll);
      gapsEl?.removeEventListener('scroll', handleGapsScroll);
      window.removeEventListener('resize', handleStrengthsScroll);
      window.removeEventListener('resize', handleGapsScroll);
    };
  }, [data?.strengths, data?.gaps]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strengths Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs overflow-hidden border-l-4 border-l-green-500 border border-slate-200 dark:border-slate-700/50">
          <div className="p-5 border-b border-slate-200 dark:border-slate-700/50 bg-green-50 dark:bg-green-900/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-lg font-semibold text-primary">Strengths</h2>
              </div>
              <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                {data?.strengths?.length || 0}
              </span>
            </div>
          </div>
          <div className="p-5 relative">
            <ul
              ref={strengthsListRef}
              className="space-y-4 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
            >
              {data?.strengths?.length ? (
                data.strengths.map((strength, index) => (
                  <li
                    key={`strength-${index}-${strength.id}`}
                    className="flex items-start gap-3 group"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-primary group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                        {strength.title}
                      </div>
                      <div className="text-sm text-secondary mt-0.5">{strength.description}</div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-sm text-secondary italic flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                  No strengths data available
                </li>
              )}
            </ul>
            {/* Overflow fade */}
            {showStrengthsOverflow && (
              <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-linear-to-t from-white dark:from-slate-900 to-transparent" />
            )}
          </div>
        </div>

        {/* Gaps Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs overflow-hidden border-l-4 border-l-red-500 border border-slate-200 dark:border-slate-700/50">
          <div className="p-5 border-b border-slate-200 dark:border-slate-700/50 bg-red-50 dark:bg-red-900/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <AlertOctagon className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-lg font-semibold text-primary">Critical Gaps</h2>
              </div>
              <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                {data?.gaps?.length || 0}
              </span>
            </div>
          </div>
          <div className="p-5 relative">
            <ul
              ref={gapsListRef}
              className="space-y-4 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
            >
              {data?.gaps?.length ? (
                data.gaps.map((gap, index) => (
                  <li key={`gap-${index}-${gap.id}`} className="flex items-start gap-3 group">
                    <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-primary group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                          {gap.title}
                        </span>
                        {gap.severity && (
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded ${
                              gap.severity === 'critical'
                                ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                : gap.severity === 'high'
                                  ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                                  : gap.severity === 'medium'
                                    ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {gap.severity.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-secondary mt-0.5">{gap.description}</div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-sm text-secondary italic flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                  No gaps data available
                </li>
              )}
            </ul>
            {/* Overflow fade */}
            {showGapsOverflow && (
              <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-linear-to-t from-white dark:from-slate-900 to-transparent" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightsGrid;
